/**
 * 图片预览网格组件
 * 只显示前 N 张图片，超出部分显示 +N 徽章
 * 支持展开查看全部图片
 *
 * ==================== 性能优化说明（支持 1 万张图片上传） ====================
 *
 * 1. 接收预处理的数据
 *    - 接收 UploadFileItemWithPreview 类型，预览 URL 已由调用方延迟创建
 *    - 只接收需要显示的文件（前 10 张），避免处理 10,000 个文件
 *
 * 2. 支持 totalCount 属性
 *    - 溢出数量基于 totalCount 计算，而非 items.length
 *    - 允许调用方传入真实的总数，组件只处理可见项
 *
 * 3. 移除 AnimatePresence
 *    - 避免大量元素同时触发退出动画导致 DOM 冲突
 *    - 保留简单的入场动画效果
 *
 * ==========================================================================
 */

import type { ReactElement } from 'react'
import { useMemo } from 'react'
import { motion } from 'framer-motion'
import { X, Loader2, Check, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { UploadFileItemWithPreview } from '@/stores/upload'

// ==================== 常量配置 ====================

/** 默认显示的最大图片数量 */
const DEFAULT_MAX_VISIBLE = 10

// ==================== 类型定义 ====================

interface ImagePreviewGridProps {
  /** 文件列表（带预览 URL） */
  items: UploadFileItemWithPreview[]
  /** 移除文件回调 */
  onRemove?: (id: string) => void
  /** 是否禁用 */
  disabled?: boolean
  /** 最大显示数量，默认 10 */
  maxVisible?: number
  /** 单个项目尺寸（px），默认 56 */
  itemSize?: number
  /** 间距（px），默认 8 */
  gap?: number
  /** 总文件数量（用于计算溢出，默认使用 items.length） */
  totalCount?: number
}

interface ImagePreviewItemProps {
  item: UploadFileItemWithPreview
  size: number
  onRemove?: ((id: string) => void) | undefined
  disabled?: boolean
}

interface OverflowBadgeProps {
  count: number
  size: number
}

// ==================== 子组件 ====================

/**
 * 单个图片预览项
 * @param props 组件属性
 * @returns 组件元素
 */
function ImagePreviewItem({
  item,
  size,
  onRemove,
  disabled = false,
}: ImagePreviewItemProps): ReactElement {
  // 使用已上传的 URL 或预览 URL
  const imageUrl = item.url ?? item.previewUrl

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
      className="group relative overflow-hidden rounded-lg bg-muted shadow-sm"
      style={{ width: size, height: size }}
    >
      {/* 图片 */}
      <img
        src={imageUrl}
        alt=""
        className="h-full w-full object-cover"
        loading="lazy"
      />

      {/* 状态覆盖层 */}
      {item.status === 'uploading' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-[1px]">
          <Loader2 className="h-5 w-5 animate-spin text-white" />
        </div>
      )}

      {item.status === 'success' && (
        <div className="absolute bottom-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-green-500 shadow-sm">
          <Check className="h-3 w-3 text-white" />
        </div>
      )}

      {item.status === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-[1px]">
          <AlertCircle className="h-5 w-5 text-red-400" />
        </div>
      )}

      {/* 删除按钮 */}
      {!disabled && item.status !== 'uploading' && onRemove !== undefined && (
        <Button
          type="button"
          variant="destructive"
          size="icon"
          onClick={(e): void => {
            e.stopPropagation()
            onRemove(item.id)
          }}
          className="absolute right-1 top-1 h-5 w-5 opacity-0 shadow-sm transition-opacity group-hover:opacity-100"
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </motion.div>
  )
}

/**
 * 溢出数量徽章
 * @param props 组件属性
 * @returns 组件元素
 */
function OverflowBadge({
  count,
  size,
}: OverflowBadgeProps): ReactElement {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
      className={cn(
        'flex items-center justify-center rounded-lg',
        'bg-gradient-to-br from-primary/20 to-primary/10',
        'border-2 border-dashed border-primary/30',
        'text-primary font-semibold'
      )}
      style={{ width: size, height: size }}
    >
      <span className="text-base font-bold">+{count}</span>
    </motion.div>
  )
}

// ==================== 主组件 ====================

/**
 * 图片预览网格
 * 只显示前 N 张图片，超出部分显示 +N 徽章
 * @param props 组件属性
 * @returns 组件元素
 */
export function ImagePreviewGrid({
  items,
  onRemove,
  disabled = false,
  maxVisible = DEFAULT_MAX_VISIBLE,
  itemSize = 56,
  gap = 6,
  totalCount,
}: ImagePreviewGridProps): ReactElement | null {
  // 计算显示的图片和溢出数量
  // 使用 totalCount（如果提供）来计算溢出，否则使用 items.length
  const { visibleItems, overflowCount, hasOverflow } = useMemo(() => {
    const actualTotal = totalCount ?? items.length
    const overflow = Math.max(0, actualTotal - maxVisible)
    const hasOverflowFlag = overflow > 0

    // 只显示前 maxVisible 张（为 +N 徽章预留位置）
    const visible = hasOverflowFlag
      ? items.slice(0, maxVisible - 1)
      : items

    return {
      visibleItems: visible,
      overflowCount: hasOverflowFlag ? actualTotal - (maxVisible - 1) : 0,
      hasOverflow: hasOverflowFlag,
    }
  }, [items, maxVisible, totalCount])

  if (items.length === 0) {
    return null
  }

  return (
    <div
      className="flex flex-wrap rounded-lg border bg-muted/20 p-2"
      style={{ gap }}
    >
      {visibleItems.map((item) => (
        <ImagePreviewItem
          key={item.id}
          item={item}
          size={itemSize}
          onRemove={onRemove}
          disabled={disabled}
        />
      ))}

      {/* 溢出徽章 */}
      {hasOverflow && (
        <OverflowBadge
          count={overflowCount}
          size={itemSize}
        />
      )}
    </div>
  )
}
